import { AxiosInstance } from 'axios';
import { getDataFastIds } from '@/lib/datafast';
import type { BillingInterval } from '@/components/billing/BillingIntervalToggle';
import {
  AutoRechargeEnabledRequest,
  AutoRechargeSettingsRequest,
  BillingRequestUrls,
  SubscriptionStatusResponse,
  WalletTopUpRequest,
} from '../types/Billing.types';
import {
  BillingInfoResponse,
  BillingResponse,
  CancelPaymentSessionResponseType,
  PaginatedReceiptsResponse,
  PaymentMethodRequest,
  PaymentMethodResponse,
  PaymentMethodsListResponse,
  PaymentSessionStatusResponseType,
  PricingResponse,
  ProductsResponse,
  PromotionsResponse,
  SubscriptionPortalUrlResponse,
  SubscriptionsResponse,
  TrialEligibilityResponse,
  WalletResponse,
} from '../types/global.types';

/** Build checkout URLs — includes ReturnUrl for embedded mode + DataFast tracking. */
export const buildCheckoutUrls = (): BillingRequestUrls => ({
  SuccessUrl: `${window.location.origin}/payment/success`,
  CancelUrl: `${window.location.origin}/payment/cancel`,
  ReturnUrl: `${window.location.origin}/payment/success`,
  ...getDataFastIds(),
});

export default class BillingApiClient {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getAllProducts(productType: string = 'subscription'): Promise<ProductsResponse> {
    return this.client.get('/products', {
      params: { product_type: productType },
    });
  }

  async createSubscription(
    productId: string,
    urls: BillingRequestUrls,
    interval: BillingInterval = 'month'
  ): Promise<BillingResponse> {
    return this.client.post(`/products/${productId}/subscribe`, {
      Urls: urls,
      Interval: interval,
    });
  }

  async renewSubscription(
    productId: string,
    urls: BillingRequestUrls,
    interval: BillingInterval = 'month'
  ): Promise<BillingResponse> {
    return this.client.post(`/products/${productId}/renew-subscription`, {
      Urls: urls,
      Interval: interval,
    });
  }

  async getSubscriptionPortalUrl(
    returnUrl: string = window.location.href
  ): Promise<SubscriptionPortalUrlResponse> {
    return this.client.get(`/subscription/portal`, {
      params: { returnUrl },
    });
  }

  async cancelSubscription(productId: string): Promise<void> {
    return this.client.post(`/products/${productId}/cancel-subscription`);
  }

  async createWalletTopUp(
    productId: string,
    request: WalletTopUpRequest
  ): Promise<BillingResponse> {
    return this.client.post(`/products/${productId}/wallet-top-up`, request);
  }

  /** Fast status check — queries DynamoDB only (no Stripe API calls). Use for polling. */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    return this.client.get('/subscription-status');
  }

  /** Full subscription details with pricing — calls Stripe API. Only use on billing page. */
  async getAllSubscriptions(): Promise<SubscriptionsResponse> {
    return this.client.get('/subscriptions');
  }

  async getAllWallets(): Promise<WalletResponse> {
    return this.client.get('/wallets');
  }

  async updateAutoRechargeEnabled(
    productId: string,
    request: AutoRechargeEnabledRequest
  ): Promise<void> {
    return this.client.post(`/wallets/${productId}/update-auto-recharge-enabled`, request);
  }

  async updateAutoRechargeSettings(
    productId: string,
    request: AutoRechargeSettingsRequest
  ): Promise<void> {
    return this.client.post(`/wallets/${productId}/update-auto-recharge-settings`, request);
  }

  async getTrialEligibility(): Promise<TrialEligibilityResponse> {
    return this.client.get('/trial-eligibility');
  }

  async getPricing(): Promise<PricingResponse> {
    return this.client.get('/pricing');
  }

  async getPromotions(): Promise<PromotionsResponse> {
    return this.client.get('/promotions');
  }

  async executePromotion(
    promotionId: string,
    request: BillingRequestUrls,
    interval: BillingInterval = 'month'
  ): Promise<BillingResponse> {
    return this.client.post(`/promotions/${promotionId}`, { ...request, Interval: interval });
  }

  async cancelPaymentSession(sessionId: string): Promise<CancelPaymentSessionResponseType> {
    return this.client.post(`/request/cancel-session?stripe_session_id=${sessionId}`);
  }

  async getPaymentSessionStatus(sessionId: string): Promise<PaymentSessionStatusResponseType> {
    return this.client.get(`/request/session-status?stripe_session_id=${sessionId}`);
  }

  async getDefaultPaymentMethod(): Promise<PaymentMethodResponse> {
    return this.client.get('/payment-methods/default');
  }

  async addPaymentMethod(request: PaymentMethodRequest): Promise<BillingResponse> {
    return this.client.post('/payment-methods', request);
  }

  async getReceipts(): Promise<PaginatedReceiptsResponse> {
    return this.client.get('/receipts');
  }

  async listPaymentMethods(): Promise<PaymentMethodsListResponse> {
    return this.client.get('/payment-methods');
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<void> {
    return this.client.delete(`/payment-methods/${paymentMethodId}`);
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    return this.client.post('/payment-methods/set-default', {
      PaymentMethodId: paymentMethodId,
    });
  }

  async getBillingInfo(): Promise<BillingInfoResponse> {
    return this.client.get('/billing-info');
  }

  async updateBillingInfo(data: { Name: string; Email: string }): Promise<void> {
    return this.client.put('/billing-info', data);
  }
}
